/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  TextField,
  Typography,
} from '@material-ui/core';
import api from '../../api/widget';

import {
  setMessage,
} from '../../store/authSlice';

import {
  isRequired,
  noSpecialCharacters,
  validEmail,
  noSequentialNumbers,
  validateField,
  validateForm,
} from '../../helpers/validation';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function LeadGenerationForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isPending, setIsPending] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
  });

  const formRules = {
    firstName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    lastName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    companyName: [isRequired],
    email: [isRequired, validEmail],
    phoneNumber: [isRequired],
    notes: [],
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm(formRules, formValues, formErrors, setFormErrors)) {
      setIsPending(true);

      api.generateLead(
        formValues.firstName,
        formValues.lastName,
        formValues.companyName,
        formValues.email,
        formValues.phoneNumber,
        formValues.notes,
      )
        .then(() => {
          dispatch(setMessage({ message: "Thank you! We'll be in touch!", severity: 'success' }));
          setFormValues({
            firstName: '',
            lastName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            notes: '',
          });
        })
        .catch(() => {
          dispatch(setMessage({ message: "Your information couldn't be saved. Please try again!", severity: 'error' }));
        })
        .finally(() => {
          setIsPending(false);
        });
    }
  };

  return (
    <form className={classes.form}>
      <Typography variant="h2" gutterBottom align="center">
        Get in touch!
      </Typography>
      <Grid container spacing={3}>
        <Grid align="left" item md={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="lead-firstanme"
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={formValues.firstName}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
        <Grid align="right" item md={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="lead-lastname"
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={formValues.lastName}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
        </Grid>
      </Grid>
      <TextField
        variant="outlined"
        margin="normal"
        id="lead-companyname"
        label="Company Name"
        name="companyName"
        value={formValues.companyName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.companyName}
        helperText={formErrors.companyName}
        disabled={isPending}
        fullWidth
        required
      />
      <TextField
        variant="outlined"
        margin="normal"
        id="lead-email"
        label="Email"
        name="email"
        autoComplete="email"
        value={formValues.email}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!formErrors.email}
        helperText={formErrors.email}
        disabled={isPending}
        fullWidth
        required
      />
      <TextField
        variant="outlined"
        margin="normal"
        id="lead-phone"
        label="Phone Number"
        name="phoneNumber"
        autoComplete="tel"
        value={formValues.phoneNumber}
        error={!!formErrors.phoneNumber}
        helperText={formErrors.phoneNumber}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isPending}
        fullWidth
        required
      />
      <TextField
        variant="outlined"
        margin="normal"
        id="lead-notes"
        label="Notes"
        name="notes"
        value={formValues.notes}
        error={!!formErrors.notes}
        helperText={formErrors.notes}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isPending}
        minRows={3}
        multiline
        fullWidth
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={handleSubmit}
        disabled={isPending}
        fullWidth
      >
        Submit
      </Button>
    </form>
  );
}
